import React, { createContext, useContext, useState } from "react";
import { createConnection } from "../signalr/connection";

export interface Message {
  user: string;
  text: string;
  type: "group" | "private";
  timestamp: string;
}

interface ChatContextProps {
  messages: Message[];
  connect: () => void;
  sendGroupMessage: (msg: string) => void;
  sendPrivateMessage: (to: string, msg: string) => void;
  clearMessages: () => void;
  isConnected: boolean;
  username: string;
  chatId: string;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

interface ChatProviderProps {
  children: any;
  username: string;
  chatId: string;
}

export const ChatProvider = ({ children, username: initialUsername, chatId: initialChatId }: ChatProviderProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [connection, setConnection] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [username, setUsername] = useState(initialUsername);
  const [chatId, setChatId] = useState(initialChatId);

  // 🔹 Connect only when component mounts with username
  const connect = () => {
    if (isConnected || !initialUsername.trim()) return;

    const conn = createConnection(initialUsername);
    setUsername(initialUsername);

    conn.start()
      .then(() => {
        console.log("Connected to SignalR as " + initialUsername);
        setIsConnected(true);
      })
      .catch(console.error);

    setConnection(conn);
  };

  // Auto-connect when username is provided
  React.useEffect(() => {
    if (!isConnected && initialUsername.trim()) {
      connect();
    }
  }, [initialUsername]);

  // Set up event handlers only when connection is ready
  React.useEffect(() => {
    if (!connection) return;

    const handleReceiveMessage = (fromUser: string, message: string) => {
      setMessages(prev => [
        ...prev,
        {
          user: fromUser,
          text: message,
          type: "group",
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    };

    const handleClearMessages = () => {
      setMessages([]);
    };

    connection.on("ReceiveMessage", handleReceiveMessage);
    connection.on("ClearMessages", handleClearMessages);

    // Cleanup: remove listeners when connection changes or component unmounts
    return () => {
      connection.off("ReceiveMessage", handleReceiveMessage);
      connection.off("ClearMessages", handleClearMessages);
    };
  }, [connection]);

  const sendGroupMessage = (msg: string) => {
    if (!isConnected || !connection) return alert("Not connected!");
    connection.invoke("SendMessage", username, msg);
  };

  const sendPrivateMessage = (to: string, msg: string) => {
    if (!isConnected || !connection) return alert("Not connected!");
    connection.invoke("SendPrivateMessage", username, to, msg);
  };

  const clearMessages = () => {
    if (!isConnected || !connection) return alert("Not connected!");
    connection.invoke("ClearMessages");
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        connect,
        sendGroupMessage,
        sendPrivateMessage,
        clearMessages,
        isConnected,
        username,
        chatId
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be inside ChatProvider");
  return ctx;
};
